<script setup lang="ts">
/**
 * Counter tile figure that ticks up the first time it is scrolled into view.
 *
 * It takes the finished string (`"1,200+"`, `"10 min"`, `"3"`) rather than a
 * number so the counters keep whatever `formatCount` decided — the prefix and
 * suffix are held fixed and only the digits animate. Anything without digits,
 * reduced motion and a missing IntersectionObserver all fall back to printing
 * the value as it came in.
 */
const props = withDefaults(defineProps<{
  value: string
  /** Milliseconds for the whole count. */
  duration?: number
}>(), {
  duration: 1400,
})

const el = ref<HTMLElement | null>(null)
const display = ref(props.value)

/**
 * `"1,200+"` → prefix `""`, number `1200`, suffix `"+"`.
 * The digit group deliberately stops at whitespace so `"10 min"` keeps its
 * space in the suffix.
 */
const parts = computed(() => {
  const match = props.value.match(/^(\D*?)(\d[\d,]*)(.*)$/s)
  if (!match) return null

  const [, prefix = '', figure = '', suffix = ''] = match

  const digits = Number(figure.replace(/,/g, ''))
  if (!Number.isFinite(digits)) return null

  return { prefix, value: digits, suffix }
})

let frame = 0
let observer: IntersectionObserver | null = null

function run() {
  const target = parts.value
  if (!target) return

  const start = performance.now()

  const step = (now: number) => {
    const progress = Math.min((now - start) / props.duration, 1)
    // easeOutExpo: fast off the mark, long settle — reads as a counter, not a slide.
    const eased = progress === 1 ? 1 : 1 - 2 ** (-10 * progress)
    const current = Math.round(target.value * eased)

    display.value = `${target.prefix}${current.toLocaleString('en-US')}${target.suffix}`

    if (progress < 1) frame = requestAnimationFrame(step)
  }

  frame = requestAnimationFrame(step)
}

watch(() => props.value, (next) => {
  display.value = next
})

onMounted(() => {
  const motionOk = !window.matchMedia('(prefers-reduced-motion: reduce)').matches
  if (!motionOk || !parts.value || !('IntersectionObserver' in window) || !el.value) return

  display.value = `${parts.value.prefix}0${parts.value.suffix}`

  observer = new IntersectionObserver((entries) => {
    if (!entries.some(entry => entry.isIntersecting)) return
    observer?.disconnect()
    observer = null
    run()
  }, { threshold: 0.4 })

  observer.observe(el.value)
})

onUnmounted(() => {
  cancelAnimationFrame(frame)
  observer?.disconnect()
})
</script>

<template>
  <span ref="el" class="tabular-nums">{{ display }}</span>
</template>
